import _ from 'lodash-es'

/**
 * Check if a string is null or empty
 */
export function isNullOrEmpty(s: string | null) {
  return s == null || s == ''
}

/**
 * Convert a string to be title case
 */
export function toTitleCase(s: string) {
  if (!s) {
    return s
  }

  const result = s.replace(/([A-Z])/g, ' $1')

  return _.startCase(result)
}

/**
 *
 */
export /**
 * Converts a string to a list of numbers.
 *
 * Example usage:
 *  - convertStringToNumber('1') -> [1]
 *  - convertStringToNumber('1.0') -> [1]
 *  - convertStringToNumber('1,2,3') -> [1,2,3]
 */
function fromStringListToNumberArray(str: string): number[] {
  if (!str) {
    return []
  }

  const result = fromStringToStringList(str).map(s => Number(s))

  return result
}

/**
 * Converts a string to a string array
 *
 * Example Usage:
 *  - convertStringToStringArray('1,2,3') -> ['1', '2', '3']
 *  - convertStringToStringArray('1 2 3') -> ['1', '2', '3']
 */
export function fromStringToStringList(str: string): string[] {
  if (!str) {
    return []
  }

  const result = splitData(str)
    .map(s => s.trim())
    .filter(s => s !== '')

  return result
}

/**
 * Converts a string to a number for metadata values
 */
export const fromMetaStringToNumber = (str: string | null | unknown) => {
  if (!str) return

  if (Number(str) === 0) {
    return Number(str)
  } else if (Math.abs(Number(str)) < 1) {
    return Number(str).toFixed(4)
  } else {
    return Math.round(Number(str)).toLocaleString()
  }
}

/**
 * Checks if a string is a valid number or a CSV of numbers.
 *
 * Usage:
 *  - isValidNumberOrListOfNumbers('1') -> true
 *  - isValidNumberOrListOfNumbers('1,2,3') -> true
 *  - isValidNumberOrListOfNumbers('1 2 3') -> true
 *  - isValidNumberOrListOfNumbers('1,2,3,') -> false
 */
export const isNumberOrCSVOfNumbers = (str: string) => {
  if (!str) return false

  const split = splitData(str.toString())
  const validNumbers = split.map(x => Number(x)).filter(x => !isNaN(x))

  return validNumbers.length === split.length
}

/**
 * Splits a string into list of substrings, delimited by the standard separators.
 * The standard separators are any whitespace character (space, tab, newline, carriage returns), commas, and semi-colons.
 * This function is safe. When an input is an empty string, null, or undefined, this will simply return an empty array.
 * Additionally, the resulting array will not contain any elements which are empty strings.
 *
 * This function was written as a replacement for Northfork's `splitData` function, which will only split on a single delimiter pattern.
 *
 * @param str Data to split by standard separators.
 * @returns Data split into substrings, or an empty array when input is falsy.
 */
export const splitData = (str: string | null | undefined): string[] => {
  if (!str) {
    return []
  }

  const delimiterRegex = /[\s;,]+/

  return str.split(delimiterRegex).filter(s => s)
}

export const splitNumbers = (str: string | null | undefined): number[] => {
  return splitData(str).map(s => parseInt(s))
}

export const splitWords = (str: string | null | undefined): string[] => {
  if (!str) {
    return []
  }

  return str.split(/[\s]/g).filter(s => s.length !== 0)
}

export const countWords = (str: string | null | undefined): number => {
  return splitWords(str).length
}
